import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { FaEye } from "react-icons/fa6";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import {
  clearFilterAdminWallet,
  setFilterAdminWallet,
} from "./adminWalletUserSlice";
import { getAllAdminWalletUsers } from "./services/adminWalletUser.services";
import { useNavigate } from "react-router-dom";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import { EDIT_ACCESS } from "../../_contstants/common";

const AdminWalletUser: React.FC = () => {
  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Admin Wallet Users
          </Typography.h4>
        </GridLayoutItem>
        <GridLayoutItem>
          <AdminWalletUserGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const AdminWalletUserGridComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);

  const AdminWalletList = useAppSelector(
    (state) => state.adminWalletUser.AdminWalletList
  );
  const loading = useAppSelector((state) => state.adminWalletUser.loading);
  const FilterAdminWallet = useAppSelector(
    (state) => state.adminWalletUser.FilterAdminWallet
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);

  useEffect(() => {
    return () => {
      dispatch(setCurrentPage(0));
      dispatch(clearFilterAdminWallet());
    };
  }, []);

  useEffect(() => {
    const payload = {
      ...FilterAdminWallet,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllAdminWalletUsers(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const MyEditCommandCell = (props: any) => {
    const handleView = (UserID: number) => {
      navigate("/adminwallettransaction", {
        state: {
          UserID: UserID,
        },
      });
    };

    return (
      <td
        {...props.tdProps}
        className={`action-td ${props.className}`}
        style={{ ...props.tdProps.style }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            themeColor="tertiary"
            size="small"
            title="View"
            onClick={() => handleView(props.dataItem.UserID)}
          >
            <FaEye className="absolute-position" style={{ fontSize: "20px" }} />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 5 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const CreditNoteFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.adminWalletUser.FilterAdminWallet}
        setFilterAction={setFilterAdminWallet}
        clearFilterAction={clearFilterAdminWallet}
        fetchDataAction={getAllAdminWalletUsers}
        pagination={{ case: "capitalize" }}
      />
    ),
    []
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}

      <KendoGrid
        scrollable="scrollable"
        style={{ maxHeight: "calc(100vh - 152px)" }}
        filterable={true}
        filter={filter}
        data={filter ? filterBy(AdminWalletList, filter) : AdminWalletList}
        onFilterChange={handleFilterChange}
        total={AdminWalletList[0]?.ItemCount || AdminWalletList.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
        className="responsive-table"
      >
        <Column
          field="UserName"
          title="Name"
          width={150}
          cells={{
            data: (props) => (
              <td>
                {props.dataItem.first_name} {props.dataItem.last_name}
              </td>
            ),
            filterCell: CreditNoteFilterCell,
          }}
        />
        <Column
          field="mobile_no"
          title="Mobile No"
          width={120}
          cells={{
            filterCell: CreditNoteFilterCell,
          }}
        />
        <Column
          field="email_id"
          title="Email ID"
          width={250}
          cells={{
            filterCell: CreditNoteFilterCell,
          }}
        />
        <Column
          field="wallet_createdon"
          title="Wallet Creation Date"
          width={150}
          cells={{
            filterCell: CreditNoteFilterCell,
          }}
        />
        <Column
          field="wallet_balance"
          title="Amount"
          width={150}
          cells={{
            data: (props) => (
              <td>
                <div className="text-end normalName">
                  {props.dataItem.wallet_balance
                    ? formatIndianNumber(+props.dataItem.wallet_balance)
                    : 0}
                </div>
              </td>
            ),
          }}
        />
        {isEditAccess && (
          <Column
            field="ID"
            title="Actions"
            cells={{ data: MyEditCommandCell }}
            width={90}
            filterable={false}
            locked={true}
          />
        )}
      </KendoGrid>
    </>
  );
};

export default AdminWalletUser;
